import type { UserStateMap } from '../../types/user-state';
import { isSavedCompanyKey } from '../../types/user-state';
import { parseSavedCompanyItemKey } from './saved-company-item-key';
import { isRelationshipTag, type RelationshipTag } from '../job-tracking/relationship-tag';
import { parseWatchlistSeen, type WatchlistSeenSnapshot } from './watchlist-seen-snapshot';
import { parseWatchlistScoreSeen, type WatchlistScoreSeenSnapshot } from './watchlist-score-seen-snapshot';
import { parseThresholdAlertConfig, type ThresholdAlertConfig } from './threshold-alert';

export interface FollowedCompany {
  companyId: string;
  companyName?: string;
  followedAt?: string;
  notes?: string;
  relationshipTag?: RelationshipTag;
  watchlistSeen?: WatchlistSeenSnapshot;
  watchlistScoreSeen?: WatchlistScoreSeenSnapshot;
  thresholdAlert?: ThresholdAlertConfig;
}

/**
 * Followed companies live in the same generic UserState map every page already hydrates
 * (no dedicated Redux slice needed, unlike jobs) — this pulls the `savedCompany#*` entries
 * back out, most-recently-followed first.
 */
export function extractFollowedCompaniesFromUserState(userState: UserStateMap): FollowedCompany[] {
  const companies: FollowedCompany[] = [];

  for (const [itemKey, entry] of Object.entries(userState)) {
    if (!isSavedCompanyKey(itemKey)) continue;
    const parsedId = parseSavedCompanyItemKey(itemKey);
    if (!parsedId) continue;
    const data = entry.data as Record<string, unknown>;
    const companyId = typeof data.companyId === 'string' && data.companyId ? data.companyId : parsedId;
    companies.push({
      companyId,
      companyName: typeof data.companyName === 'string' ? data.companyName : undefined,
      followedAt: typeof data.followedAt === 'string' ? data.followedAt : undefined,
      notes: typeof data.notes === 'string' ? data.notes : undefined,
      relationshipTag: isRelationshipTag(data.relationshipTag) ? data.relationshipTag : undefined,
      watchlistSeen: parseWatchlistSeen(data.watchlistSeen) ?? undefined,
      watchlistScoreSeen: parseWatchlistScoreSeen(data.watchlistScoreSeen) ?? undefined,
      thresholdAlert: parseThresholdAlertConfig(data.thresholdAlert) ?? undefined,
    });
  }

  companies.sort((a, b) => (b.followedAt ?? '').localeCompare(a.followedAt ?? ''));
  return companies;
}
